import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card } from "@/components/ui/card";
import SubjectAutocomplete from "@/components/app/SubjectAutocomplete";
import { CURRICULA } from "./constants";

// Left-hand profile card for Quick Check. Renders the curriculum picker
// + all grade inputs + activities slider + intended-degree autocomplete
// + SAT/ACT / IELTS gating toggles + the submit button.
//
// Kept dumb — receives `form`, `update`, `submit`, `loading`, `hasResults`.
// State lives in the parent `AdmissionCalculator` so form edits still
// invalidate the debounced re-classification effect there.
export default function GradesForm({ form, update, submit, loading, hasResults, signedIn, onSaveProfile, savingProfile, profileDrift, prefilledFromJourney, gradesConfirmed, onConfirmGrades, draftFromSaved, prefilledConfirmed }) {
  const curr = form.curriculum;

  return (
    <Card className="lg:col-span-2 p-6 border-stone-200 bg-white h-fit">
      <h2 className="font-editorial text-2xl">Your profile</h2>

      <div className="mt-5 space-y-4">
        <div>
          <Label className="label-uppercase">Curriculum</Label>
          <Select value={form.curriculum} onValueChange={(v) => update("curriculum", v)}>
            <SelectTrigger data-testid="calc-curriculum-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRICULA.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {curr === "a_level" && (
          <div>
            <Label className="label-uppercase">Predicted A Level grades</Label>
            <Input data-testid="calc-alevel-input" className="mt-1 font-mono"
                   value={form.a_level} onChange={(e) => update("a_level", e.target.value)}
                   placeholder="e.g. A*A*A" />
          </div>
        )}
        {curr === "ib" && (
          <div>
            <Label className="label-uppercase">Predicted IB total (24–45)</Label>
            <Input data-testid="calc-ib-input" className="mt-1 font-mono" type="number" min="24" max="45"
                   value={form.ib} onChange={(e) => update("ib", Number(e.target.value))} />
          </div>
        )}
        {curr === "ib" && (
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="label-uppercase">Maths grade (1–7)</Label>
              <Input data-testid="calc-ib-maths-input" className="mt-1 font-mono" type="number" min="1" max="7"
                     placeholder="e.g. 7"
                     value={form.ib_maths}
                     onChange={(e) => update("ib_maths", e.target.value === "" ? "" : Number(e.target.value))} />
            </div>
            <div>
              <Label className="label-uppercase">English grade (1–7)</Label>
              <Input data-testid="calc-ib-english-input" className="mt-1 font-mono" type="number" min="1" max="7"
                     placeholder="optional"
                     value={form.ib_english}
                     onChange={(e) => update("ib_english", e.target.value === "" ? "" : Number(e.target.value))} />
            </div>
          </div>
        )}
        {curr === "atar" && (
          <div>
            <Label className="label-uppercase">Predicted ATAR (0.00–99.95)</Label>
            <Input data-testid="calc-atar-input" className="mt-1 font-mono"
                   type="number" min="0" max="99.95" step="0.05"
                   value={form.atar}
                   onChange={(e) => update("atar", e.target.value === "" ? "" : Number(e.target.value))}
                   placeholder="e.g. 88.50" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              The ATAR is Australia's percentile-based admissions rank —
              e.g. an ATAR of <span className="font-mono">90</span> means
              you're in the top 10% of Year 12 finishers.
            </p>
          </div>
        )}
        {curr === "a_level" && (
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="label-uppercase">Maths A-level</Label>
              <Input data-testid="calc-alevel-maths-input" className="mt-1 font-mono"
                     placeholder="A* / A / B …"
                     value={form.a_level_maths}
                     onChange={(e) => update("a_level_maths", e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label className="label-uppercase">English A-level</Label>
              <Input data-testid="calc-alevel-english-input" className="mt-1 font-mono"
                     placeholder="A* / A / B …"
                     value={form.a_level_english}
                     onChange={(e) => update("a_level_english", e.target.value.toUpperCase())} />
            </div>
          </div>
        )}
        {curr === "cbse" && (
          <div>
            <Label className="label-uppercase">CBSE overall % (0–100)</Label>
            <Input data-testid="calc-cbse-input" className="mt-1 font-mono" type="number" min="0" max="100"
                   value={form.cbse} onChange={(e) => update("cbse", Number(e.target.value))} />
          </div>
        )}
        {curr === "isc" && (
          <div>
            <Label className="label-uppercase">ISC overall % (0–100)</Label>
            <Input data-testid="calc-isc-input" className="mt-1 font-mono" type="number" min="0" max="100"
                   step="0.5"
                   value={form.isc} onChange={(e) => update("isc", Number(e.target.value))}
                   placeholder="e.g. 90" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              ISC (CISCE) marks slightly stricter than CBSE — we adjust
              when comparing to overseas offers.
            </p>
          </div>
        )}
        {curr === "ap_composite" && (
          <div>
            <Label className="label-uppercase">AP composite (1.0–5.0)</Label>
            <Input data-testid="calc-ap-input" className="mt-1 font-mono" type="number"
                   min="1" max="5" step="0.05"
                   value={form.ap_composite}
                   onChange={(e) => update("ap_composite", Number(e.target.value))}
                   placeholder="e.g. 4.2" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              Average score across your AP exams (assumes ≥3 exams sat).
            </p>
          </div>
        )}
        {curr === "abitur" && (
          <div>
            <Label className="label-uppercase">Abitur average (1.0–4.0)</Label>
            <Input data-testid="calc-abitur-input" className="mt-1 font-mono" type="number"
                   min="1.0" max="4.0" step="0.1"
                   value={form.abitur}
                   onChange={(e) => update("abitur", Number(e.target.value))}
                   placeholder="e.g. 1.7" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              German Abitur scale — <span className="font-mono">1.0</span>
              {" "}is the top mark, <span className="font-mono">4.0</span>
              {" "}is the pass floor.
            </p>
          </div>
        )}
        {curr === "french_bac" && (
          <div>
            <Label className="label-uppercase">Baccalauréat (0–20)</Label>
            <Input data-testid="calc-bac-input" className="mt-1 font-mono" type="number"
                   min="0" max="20" step="0.5"
                   value={form.french_bac}
                   onChange={(e) => update("french_bac", Number(e.target.value))}
                   placeholder="e.g. 15" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              Overall out of 20 — <span className="font-mono">12</span> Assez
              Bien, <span className="font-mono">14</span> Bien,{" "}
              <span className="font-mono">16</span> Très Bien.
            </p>
          </div>
        )}
        {curr === "hkdse" && (
          <div>
            <Label className="label-uppercase">HKDSE best-5 sum (0–35)</Label>
            <Input data-testid="calc-hkdse-input" className="mt-1 font-mono" type="number"
                   min="0" max="35" step="1"
                   value={form.hkdse}
                   onChange={(e) => update("hkdse", Number(e.target.value))}
                   placeholder="e.g. 28" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              Sum of your best-5 subject scores
              ({" "}<span className="font-mono">5**=7</span>,{" "}
              <span className="font-mono">5*=6</span>,{" "}
              <span className="font-mono">5=5</span>{" "}…{" "}).
            </p>
          </div>
        )}
        {curr === "suneung" && (
          <div>
            <Label className="label-uppercase">Suneung / CSAT percentile average (0–100)</Label>
            <Input data-testid="calc-suneung-input" className="mt-1 font-mono" type="number"
                   min="0" max="100" step="1"
                   value={form.suneung}
                   onChange={(e) => update("suneung", Number(e.target.value))}
                   placeholder="e.g. 92" />
            <p className="mt-1 text-[11px] text-stone-500 leading-snug">
              National-percentile average across your top 3 sections
              (typically Korean, Math and English).{" "}
              <span className="font-mono">99</span> = top 1% cohort (SNU / KAIST tier),{" "}
              <span className="font-mono">88</span> = top 12% (SKY range).
            </p>
          </div>
        )}
        {curr === "american_diploma" && (
          <div>
            <Label className="label-uppercase">American Diploma GPA (0–4)</Label>
            <Input data-testid="calc-adgpa-input" className="mt-1 font-mono" type="number" step="0.01" min="0" max="4"
                   value={form.american_diploma} onChange={(e) => update("american_diploma", Number(e.target.value))} />
          </div>
        )}
        {curr === "gpa" && (
          <div>
            <Label className="label-uppercase">GPA (0–4)</Label>
            <Input data-testid="calc-gpa-input" className="mt-1 font-mono" type="number" step="0.01" min="0" max="4"
                   value={form.gpa} onChange={(e) => update("gpa", Number(e.target.value))} />
          </div>
        )}

        {onConfirmGrades && (gradesConfirmed ? (
          <p className="text-[11px] text-emerald-800" data-testid="calc-grades-confirmed">
            {prefilledConfirmed
              ? "Filled in from the grades you confirmed earlier. Change them any time."
              : "Using these as your curriculum and predicted grades."}
          </p>
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="calc-grades-unconfirmed">
            {draftFromSaved
              ? "Filled in from grades saved earlier. They aren't confirmed yet: edit them, or confirm they're right."
              : "These are starting values, not your answers yet. Edit them, or confirm they're right."}
            <button type="button" onClick={onConfirmGrades} data-testid="calc-confirm-grades"
              className="mt-1.5 block min-h-[36px] rounded-full border border-amber-400 bg-white px-3 text-[12px] font-medium text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
              Yes, these are my grades
            </button>
          </div>
        ))}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase">Activities strength — {form.activities_strength}/5</Label>
            <Slider data-testid="calc-activities-slider" min={1} max={5} step={1}
                    value={[form.activities_strength]}
                    onValueChange={(v) => update("activities_strength", v[0])}
                    className="mt-3" />
            <div className="text-xs text-stone-500 mt-1 font-mono">1 = none · 3 = solid · 5 = national/international impact</div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label className="label-uppercase">Intended degrees (optional)</Label>
              {prefilledFromJourney && (
                <span
                  data-testid="calc-degree-prefilled-hint"
                  className="text-[10px] text-[#7C3AED] font-medium inline-flex items-center gap-0.5"
                  title="Prefilled from the subjects you confirmed at the Pick stage"
                >
                  ✨ From your Pick
                </span>
              )}
            </div>
            <div className="mt-1">
              <SubjectAutocomplete
                values={form.intended_degrees}
                onChange={(vs) => update("intended_degrees", vs)}
                placeholder="Type to add more — e.g. Politics, Economics…"
                testIdPrefix="calc-degree-picker"
                maxChips={5}
              />
            </div>
          </div>
        </div>

        {/* Context toggles — decide which tests to ask for */}
        <div className="rounded-md border border-stone-200 bg-[#F3F1EC] p-3 space-y-3">
          <div className="label-uppercase !text-[10px]">Which tests apply to you?</div>
          <div className="flex items-center justify-between">
            <div className="pr-3">
              <div className="text-sm">English is my school's language of instruction</div>
              <div className="text-xs text-stone-500">If yes, IELTS/TOEFL is usually waived.</div>
            </div>
            <Switch data-testid="calc-native-english-toggle"
                    checked={form.native_english}
                    onCheckedChange={(v) => update("native_english", v)} />
          </div>
          <div className="flex items-center justify-between">
            <div className="pr-3">
              <div className="text-sm">I'm also applying to US universities</div>
              <div className="text-xs text-stone-500">Some US schools require SAT/ACT (e.g. MIT). Others are test-optional and accept A-Levels/IB alone. We show the policy on each university's page.</div>
            </div>
            <Switch data-testid="calc-applying-us-toggle"
                    checked={form.applying_to_us || form.curriculum === "american_diploma"}
                    disabled={form.curriculum === "american_diploma"}
                    onCheckedChange={(v) => update("applying_to_us", v)} />
          </div>
        </div>

        {(form.tests_not_taking || []).length > 0 && (
          <p data-testid="calc-not-taking-note" className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-md p-2">
            Your profile says you're not taking {form.tests_not_taking.map((t) => (t === "sat_act" ? "the SAT/ACT" : "an English test")).join(" or ")}.
            {" "}It's left out of your grade gap unless a university requires it.{" "}
            <a href="/profile#profile-section-grades" className="underline underline-offset-2">Change on my profile</a>
          </p>
        )}
        {(form.applying_to_us || form.curriculum === "american_diploma") && (
          <div>
            <Label className="label-uppercase">Which test have you taken?</Label>
            <ToggleGroup type="single" value={form.sat_act_status}
                         onValueChange={(v) => v && update("sat_act_status", v)}
                         className="mt-2 grid grid-cols-3 gap-1"
                         data-testid="calc-sat-act-toggle">
              <ToggleGroupItem value="sat" data-testid="sat-act-opt-sat" className="border border-stone-200">SAT</ToggleGroupItem>
              <ToggleGroupItem value="act" data-testid="sat-act-opt-act" className="border border-stone-200">ACT</ToggleGroupItem>
              <ToggleGroupItem value="not_taken" data-testid="sat-act-opt-none" className="border border-stone-200">Not taken</ToggleGroupItem>
            </ToggleGroup>
            {form.sat_act_status === "sat" && (
              <div className="mt-3">
                <Label className="label-uppercase">SAT score (400–1600)</Label>
                <Input data-testid="calc-sat-input" className="mt-1 font-mono" type="number"
                       placeholder="e.g. 1450"
                       value={form.sat} onChange={(e) => update("sat", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            )}
            {form.sat_act_status === "act" && (
              <div className="mt-3">
                <Label className="label-uppercase">ACT score (1–36)</Label>
                <Input data-testid="calc-act-input" className="mt-1 font-mono" type="number"
                       placeholder="e.g. 32"
                       value={form.act} onChange={(e) => update("act", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            )}
            {form.sat_act_status === "not_taken" && (
              <div className="mt-3 text-xs text-stone-500 font-mono">
                Fine for now — we'll classify without a test score. US universities that require it (e.g. MIT) will show a warning on their profile.
              </div>
            )}
          </div>
        )}

        {!form.native_english && (
          <div>
            <Label className="label-uppercase">Which English test have you taken?</Label>
            <ToggleGroup type="single" value={form.english_test_status}
                         onValueChange={(v) => v && update("english_test_status", v)}
                         className="mt-2 grid grid-cols-3 gap-1"
                         data-testid="calc-english-test-toggle">
              <ToggleGroupItem value="ielts" data-testid="english-opt-ielts" className="border border-stone-200">IELTS</ToggleGroupItem>
              <ToggleGroupItem value="toefl" data-testid="english-opt-toefl" className="border border-stone-200">TOEFL</ToggleGroupItem>
              <ToggleGroupItem value="not_taken" data-testid="english-opt-none" className="border border-stone-200">Not taken</ToggleGroupItem>
            </ToggleGroup>
            {form.english_test_status === "ielts" && (
              <div className="mt-3">
                <Label className="label-uppercase">IELTS score (0–9)</Label>
                <Input data-testid="calc-ielts-input" className="mt-1 font-mono" type="number" step="0.5"
                       placeholder="e.g. 7.0"
                       value={form.ielts} onChange={(e) => update("ielts", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            )}
            {form.english_test_status === "toefl" && (
              <div className="mt-3">
                <Label className="label-uppercase">TOEFL score (0–120)</Label>
                <Input data-testid="calc-toefl-input" className="mt-1 font-mono" type="number"
                       placeholder="e.g. 100"
                       value={form.toefl} onChange={(e) => update("toefl", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            )}
            {form.english_test_status === "not_taken" && (
              <div className="mt-3 text-xs text-stone-500 font-mono">
                Fine for now — we'll classify without an English score.
              </div>
            )}
          </div>
        )}

        <Button data-testid="calc-submit-btn" onClick={submit} disabled={loading}
                className="w-full bg-[#002147] hover:bg-[#001A38] text-white rounded-md">
          {loading ? "Calculating…" : (hasResults ? "Re-check my chances" : "Check my chances across all universities")}
        </Button>

        {/* Save-to-profile — signed-in users only. Lets them promote
            Quick Check edits into their canonical Advanced profile
            without walking through the 4-step wizard again. Absent
            for anonymous visitors so the CTA doesn't dangle. */}
        {signedIn && onSaveProfile && (
          <div className="mt-1">
            <button
              type="button"
              onClick={onSaveProfile}
              disabled={savingProfile === "saving" || (profileDrift && Object.keys(profileDrift).length === 0)}
              data-testid="calc-save-to-profile-btn"
              className="w-full text-[11px] font-underground-sc tracking-wider text-stone-600 hover:text-[#4C1D95] disabled:opacity-60 text-center py-2 transition-colors"
            >
              {savingProfile === "saving" && "Saving to profile…"}
              {savingProfile === "saved"  && "✓ Saved to your profile"}
              {savingProfile !== "saving" && savingProfile !== "saved" && (
                profileDrift && Object.keys(profileDrift).length === 0
                  ? "✓ Matches your saved profile"
                  : "Save these grades to my profile →"
              )}
            </button>
            {profileDrift && profileDrift.hint && savingProfile !== "saving" && savingProfile !== "saved" && (
              <div
                data-testid="calc-profile-drift-hint"
                className="text-[10px] font-underground-sc tracking-wider text-stone-500 text-center pb-1 -mt-1"
              >
                Your profile still lists{" "}
                <span className="text-stone-800 normal-case tracking-normal font-semibold">
                  {profileDrift.hint}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
